import { CheckCircleOutlined, CloseCircleOutlined, NodeIndexOutlined } from "@ant-design/icons";
import { useSearch } from "@tanstack/react-router";
import { Button, Tag } from "antd";
import {
    useExportOutgoingPostbackStepLogsMutation,
    useGetOutgoingPostbackStepLogsQuery,
} from "api/TenantSlug/StepLog/hooks";
import { OutgoingPostbackStepLogDetailDrawer } from "components/Drawers/OutgoingPostbackStepLogDetailDrawer/OutgoingPostbackStepLogDetailDrawer";
import { useStepTypeLabel } from "components/UI/Selects/StepTypeSelectSearchable/useStepTypeLabel";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { StepLogMessageCell } from "components/UI/Table/UI/StepLogMessageCell/StepLogMessageCell";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { TenantLink } from "components/UI/TenantLink/TenantLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useRowHandlers } from "hooks/useTableRowHandlers";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { PermissionEnum, SearchFilterEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import GetStepLogOutgoingPostbacksResponse = App.Http.Responses.StepLog.GetStepLogOutgoingPostbacksResponse;
import StepLogOutgoingPostbackDataOptional = App.Data.StepLogOutgoingPostbackDataOptional;

interface SearchParams {
    [`outgoingPostbackStepLogDetail-id`]: string;
}

export const OutgoingPostbackStepLogTable = () => {
    const tableId = TableId.OutgoingPostbackStepLogTable;
    const { t } = usePaldockTranslation("tables.outgoing-postback-step-log");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const { t: tGA } = usePaldockTranslation("drawers.general-actions");
    const getStepTypeLabel = useStepTypeLabel();
    const tableAtomInstance = tableAtom<StepLogOutgoingPostbackDataOptional>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const exportState = useRecoilValue(exportAtom(tableId));
    const permissionsUtils = usePermission();
    const canViewOutput = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackLogViewOutput);
    const canViewAdvertiser = permissionsUtils.hasPermission(PermissionEnum.OutgoingPostbackLogViewAdvertiser);
    const createdAtStr = useCreatedAtFilterValue();
    const searchParams = useSearch({ strict: false }) as SearchParams;

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                ...(tableState.filter?.filter ?? {}),
                ...("started_at" in (tableState.filter?.filter ?? {})
                    ? {}
                    : {
                          started_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            sort: tableState.filter?.sort ?? "-started_at",
            include: [
                "outgoingPostback",
                "conversionState",
                "owner",
                canViewAdvertiser ? "advertiser" : null,
                "offer",
                "stepRun",
            ]
                .filter(Boolean)
                .join(","),
            fields: {
                step_log_outgoing_postbacks: [
                    "id,origin_eid,eid,advertiser_custom_id,owner_id,advertiser_id,offer_id,step_run_id",
                    "http_status_code,state,conversion_state_id,outgoing_postback_id,started_at,created_at",
                    ...(canViewOutput ? ["message"] : []),
                ].join(","),
                outgoingPostback: "id,name",
                conversionState: "id,type",
                owner: "id,nickname",
                ...(canViewAdvertiser ? { advertiser: "id,nickname" } : {}),
                offer: "id,title",
                stepRun: "id,scenario_run_id,title,type",
            },
        },
    };

    const getOutgoingPostbackStepLogsQuery = useGetOutgoingPostbackStepLogsQuery(request, { enabled: false });
    const getOutgoingPostbackStepLogsExportMutation = useExportOutgoingPostbackStepLogsMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetStepLogOutgoingPostbacksResponse,
        void,
        StepLogOutgoingPostbackDataOptional,
        BaseExportRequest
    >(tableId, getOutgoingPostbackStepLogsQuery, undefined, undefined, getOutgoingPostbackStepLogsExportMutation);

    const isScenarioFilterActive = !!tableState.filter?.filter?.["eid"]?.[SearchFilterEnum.Equal];
    const scenarioRunId = tableFetchDataUtils.fetchQuery.isFetching
        ? undefined
        : tableFetchDataUtils.tableData?.outgoingPostbackLogs?.[0]?.stepRun?.scenario_run_id;

    const viewScenarioRunButton = isScenarioFilterActive ? (
        scenarioRunId ? (
            <TenantLink to={`/scenario/history/${scenarioRunId}`}>
                <Button icon={<NodeIndexOutlined />}>{tGA("view_scenario_run")}</Button>
            </TenantLink>
        ) : (
            <Button icon={<NodeIndexOutlined />} disabled>
                {tGA("view_scenario_run")}
            </Button>
        )
    ) : null;

    const { getRowHandlers, getRowClassName } = useRowHandlers<StepLogOutgoingPostbackDataOptional>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.OutgoingPostbackStepLogDetail,
                drawerParam: `${DrawerId.OutgoingPostbackStepLogDetail}-id`,
                permission: PermissionEnum.StepLogOutgoingPostbacksView,
            },
        ],
    });

    const columnConfig: TableColumnType<StepLogOutgoingPostbackDataOptional>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.started")} />,
            dataIndex: "started_at",
            key: "started_at",
            render: (_, row) => <DateTimeCell value={row.started_at ?? undefined} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("created_at")} />,
            dataIndex: "created_at",
            key: "created_at",
            render: (v) => <DateTimeCell value={v} />,
            sorter: true,
            ellipsis: true,
            width: 150,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("conversion_id")} />,
            dataIndex: "origin_eid",
            key: "origin_eid",
            render: (_, row) => (row.origin_eid ? <ShortenedIdWithCopy id={row.origin_eid} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "origin_eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("event_id")} />,
            dataIndex: "eid",
            key: "eid",
            render: (_, row) => (row.eid ? <ShortenedIdWithCopy id={row.eid} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("run_id")} />,
            dataIndex: "step_run_id",
            key: "step_run_id",
            render: (_, row) => (row.step_run_id ? <ShortenedIdWithCopy id={row.step_run_id} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "step_run_id",
            ellipsis: true,
            width: 150,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("external_id")} />,
            dataIndex: "advertiser_custom_id",
            key: "advertiser_custom_id",
            render: (v) => v ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "advertiser_custom_id",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("transaction")} />,
            dataIndex: "conversionState.type",
            key: "conversionState.type",
            render: (_, row) => row.conversionState?.type ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "conversionState.type",
            ellipsis: true,
            width: 120,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("affiliate")} />,
            dataIndex: "owner.nickname",
            key: "owner.nickname",
            render: (_, row) => (
                <EntityLinkCell.Owner
                    entity={row.owner && row.owner_id ? { ...row.owner, id: row.owner_id } : undefined}
                />
            ),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "owner.nickname",
            ellipsis: true,
            width: 150,
        },
        ...(canViewAdvertiser
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={tG("advertiser")} />,
                      dataIndex: "advertiser.nickname",
                      key: "advertiser.nickname",
                      render: (_: unknown, row: StepLogOutgoingPostbackDataOptional) => (
                          <EntityLinkCell.Advertiser
                              entity={
                                  row.advertiser && row.advertiser_id
                                      ? { ...row.advertiser, id: row.advertiser_id }
                                      : undefined
                              }
                          />
                      ),
                      sorter: true,
                      filterable: true,
                      filterType: FILTER_TYPES.string,
                      filterName: "advertiser.nickname",
                      ellipsis: true,
                      width: 150,
                  } as TableColumnType<StepLogOutgoingPostbackDataOptional>,
              ]
            : []),
        {
            title: <ColumnHeaderTitle columnTitle={tG("offer")} />,
            dataIndex: "offer.title",
            key: "offer.title",
            render: (_, row) => (
                <EntityLinkCell.Offer
                    entity={row.offer && row.offer_id ? { ...row.offer, id: row.offer_id } : undefined}
                />
            ),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "offer.title",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "outgoingPostback.name",
            key: "outgoingPostback.name",
            render: (_, row) => <EntityLinkCell.OutgoingPostback entity={row.outgoingPostback} />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "outgoingPostback.name",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.step")} />,
            dataIndex: "stepRun.title",
            key: "stepRun.title",
            render: (_, row) => row.stepRun?.title ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "stepRun.title",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.type")} />,
            dataIndex: "stepRun.type",
            key: "stepRun.type",
            render: (_, row) => {
                const type = row.stepRun?.type;
                if (!type) return <EmptyCell />;
                if (type === "integration" || type === "outgoing_postback")
                    return getStepTypeLabel(type).split(" - ")[0];
                return getStepTypeLabel(type);
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.outgoingPostbackStepTypeSelect,
            filterName: "stepRun.type",
            ellipsis: true,
            width: 120,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("status")} />,
            dataIndex: "http_status_code",
            key: "http_status_code",
            render: (_, row) => row.http_status_code ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.number,
            filterName: "http_status_code",
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "state",
            key: "state",
            render: (_, row) => {
                const isOk = row.state === true;
                return (
                    <Tag icon={isOk ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={isOk ? "green" : "red"}>
                        {isOk ? tGL("ok") : tGL("fail")}
                    </Tag>
                );
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "state",
            filterOptions: [
                { label: tGL("ok"), value: "true" },
                { label: tGL("fail"), value: "false" },
            ],
            ellipsis: true,
            width: 100,
        },
        ...(canViewOutput
            ? [
                  {
                      title: <ColumnHeaderTitle columnTitle={t("columns.detail")} />,
                      dataIndex: "message",
                      key: "message",
                      render: (_: unknown, row: StepLogOutgoingPostbackDataOptional) => (
                          <StepLogMessageCell
                              message={row.message}
                              stepType={row.stepRun?.type}
                              id={`message-${row.id}`}
                          />
                      ),
                      width: 390,
                      ellipsis: true,
                  } as TableColumnType<StepLogOutgoingPostbackDataOptional>,
              ]
            : []),
    ];

    return (
        <>
            <Table<GetStepLogOutgoingPostbacksResponse, StepLogOutgoingPostbackDataOptional>
                tableId={tableId}
                columnConfig={columnConfig}
                data={tableFetchDataUtils.tableData as GetStepLogOutgoingPostbacksResponse}
                dataSource={tableFetchDataUtils.tableData?.outgoingPostbackLogs ?? []}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.id as Key}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        filter: request.filter,
                        extension: exportState.extension,
                        headers: exportState.headers,
                        page: request.page,
                        export_all: exportState.range === "all",
                        delimiter: exportState.delimiter,
                    })
                }
                enableFilters={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilter)}
                enableColumnsVisibility={permissionsUtils.hasPermission(PermissionEnum.LogColumnSetting)}
                enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilterTime)}
                dateFilterDataIndex={"started_at"}
                autoScrollWidth={true}
                hideActionsColumn={true}
                extraTopBarElements={viewScenarioRunButton}
                extraFilters={[]}
            />

            {permissionsUtils.hasPermission(PermissionEnum.StepLogOutgoingPostbacksView) &&
                searchParams[`${DrawerId.OutgoingPostbackStepLogDetail}-id`] && (
                    <OutgoingPostbackStepLogDetailDrawer
                        logId={searchParams[`${DrawerId.OutgoingPostbackStepLogDetail}-id`]}
                    />
                )}
        </>
    );
};
